import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchTenderV2 } from '../../api/v2/tenders';
import { fetchCandidatesV2 } from '../../api/v2/candidates';
import type { CandidateAPIV2, TenderAPIV2 } from '../../types';
import NotFound from '../../components/NotFound';
import StatTile from '../../components/StatTile';
import CandidateCard from '../../components/CandidateCard';

/**
 * Format an ISO LocalDate string (e.g. "2026-05-28") as a browser-locale date.
 * Returns "--" for null/invalid input.
 *
 * Important: we parse the parts manually rather than `new Date(iso)` to avoid
 * the JS Date constructor's UTC interpretation of date-only strings, which can
 * shift the day in non-UTC timezones.
 */
function formatDeadline(iso: string | null): string {
  if (!iso) return '--';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '--';
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function TenderOverview() {
  const { tenderId } = useParams<{ tenderId: string }>();
  const [tender, setTender] = useState<TenderAPIV2 | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<CandidateAPIV2[] | null>(null);
  const [candidatesLoading, setCandidatesLoading] = useState(true);

  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setCandidatesLoading(true);
    setCandidates(null);

    // Fetch tender and candidates in parallel — they're independent reads.
    fetchTenderV2(tenderId)
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setNotFound(true);
          setTender(null);
        } else {
          setTender(data);
        }
      })
      .catch(() => {
        if (!cancelled) setTender(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    fetchCandidatesV2(tenderId)
      .then((data) => {
        if (cancelled) return;
        // 404 here is handled by the tender 404 path above; treat as empty list.
        setCandidates(data?.candidates ?? []);
      })
      .catch(() => {
        if (!cancelled) setCandidates([]);
      })
      .finally(() => { if (!cancelled) setCandidatesLoading(false); });

    return () => { cancelled = true; };
  }, [tenderId]);

  // Tender UUID exists in the URL but the backend returned 404 → full 404 page.
  if (!loading && notFound) {
    return <NotFound />;
  }

  return (
    <div className="space-y-6">
      {/* Under Construction tile — kept until the v2 tender overview is fully built */}
      <div className="max-w-2xl mx-auto">
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
              Under construction
            </span>
            <span className="text-[11px] font-mono text-gray-400">v2 · /v2/tenders/{tenderId}</span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900">Tender Overview</h1>
          <p className="mt-2 text-sm text-gray-600">
            The redesigned tender overview will live here, based on the approved wireframe.
            For now, please use the classic version.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Link
              to="/tenders"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium px-3.5 py-2"
            >
              <span>←</span>
              <span>Back to classic tenders</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      {!loading && (
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <ol className="flex items-center gap-2">
            <li><Link to="/tenders" className="hover:text-brand-700">Tenders</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-800 font-medium truncate max-w-xs">
              {tender?.title || 'Not found'}
            </li>
          </ol>
        </nav>
      )}

      {/* Tender header tile — title, deadline, extracted skills */}
      {!loading && tender && (() => {
        // Defensive fallbacks — guard against an older backend or partial response.
        const skills = Array.isArray(tender.skills) ? tender.skills : [];
        const deadline = tender.submission_deadline ?? null;
        const criteria = Array.isArray(tender.criteria) ? tender.criteria : [];

        const requirements = criteria.filter((c) => c.kind === 'REQUIREMENT');
        const responsibilities = criteria.filter((c) => c.kind === 'RESPONSIBILITY');

        const reqActive = requirements.filter((c) => c.is_active).length;
        const reqTotal = requirements.length;
        const reqInactive = reqTotal - reqActive;

        const respActive = responsibilities.filter((c) => c.is_active).length;
        const respTotal = responsibilities.length;
        const respInactive = respTotal - respActive;

        return (
          <>
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <h1 className="text-2xl font-bold text-gray-900">{tender.title || 'Not found'}</h1>

              <dl className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-700">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-400" aria-hidden="true">📅</span>
                  <span>Deadline: <strong>{formatDeadline(deadline)}</strong></span>
                </div>
              </dl>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Extracted skills
                </p>
                {skills.length === 0 ? (
                  <p className="mt-2 text-xs text-gray-400">No skills</p>
                ) : (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {skills.map((skill) => (
                      <li key={skill}>
                        <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                          {skill}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            {/* Stat tiles row — Requirements / Responsibilities wired to tender data;
                Candidates / Strong fits placeholders (data flows from a future
                /v2/tenders/:id/candidates endpoint). */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile
                label="Requirements"
                icon="📋"
                big={String(reqActive)}
                small={`of ${reqTotal}`}
                subtext={
                  reqTotal === 0
                    ? undefined
                    : reqInactive > 0
                      ? `${reqInactive} deselected`
                      : 'all active'
                }
                subtextTone={reqInactive > 0 ? 'attention' : 'neutral'}
              />
              <StatTile
                label="Responsibilities"
                icon="⚙"
                big={String(respActive)}
                small={`of ${respTotal}`}
                subtext={
                  respTotal === 0
                    ? undefined
                    : respInactive > 0
                      ? `${respInactive} deselected`
                      : 'all active'
                }
                subtextTone={respInactive > 0 ? 'attention' : 'neutral'}
              />
              <StatTile
                label="Candidates"
                icon="👤"
                big="—"
                subtext="—"
              />
              <StatTile
                label="Strong fits"
                icon="★"
                big="—"
                subtext="—"
              />
            </section>

            {/* Candidate grid — 2-column on md+, sorted by match % desc.
                Only candidates with skill match >= 75% reach this list
                (filtered server-side). */}
            {!candidatesLoading && candidates !== null && (() => {
              const sorted = [...candidates].sort(
                (a, b) => (b.skills?.score ?? 0) - (a.skills?.score ?? 0),
              );
              if (sorted.length === 0) {
                return (
                  <section className="text-center py-8 text-gray-500 text-sm">
                    No candidates above 75%
                  </section>
                );
              }
              return (
                <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sorted.map((candidate) => (
                    <CandidateCard key={candidate.id} candidate={candidate} />
                  ))}
                </section>
              );
            })()}
          </>
        );
      })()}
    </div>
  );
}
